import React, { useState, useEffect } from 'react';
import Header from './Header';
import LoanForm from './LoanForm';
import FormSuccess from './FormSuccess';
import LoadingScreen from './ui/LoadingScreen';
import Alert from './ui/Alert';
import Faq from './FAQ';
import Contact from './Contact';
import AboutUs from './AboutUs';
import Footer from './Footer';
import AnimatedUseCases from './ui/AnimatedUseCases';
import FloatingActionButton from './ui/FloatingActionButton';
import Features from './Features';
import LoanTypes from './LoanTypes';
import { LoanFormData, AlertState, LoadingState } from '../types';
import i18n from '../i18n/config';

const LandingPage: React.FC = () => {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [loading, setLoading] = useState<LoadingState>({ isLoading: false });
  const [alert, setAlert] = useState<AlertState>({ visible: false, type: 'info', message: '' });
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [currentLanguage, setCurrentLanguage] = useState(i18n.locale || 'lv');
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsPageLoading(false);
    }, 1500);
    
    setCurrentLanguage(i18n.locale || document.documentElement.lang || 'lv');
    
    return () => clearTimeout(timer);
  }, []);
  
  const handleFormSubmit = (formData: LoanFormData) => {
    setFormSubmitted(true);
    setAlert({
      visible: true,
      type: 'success',
      message: i18n.t('form.messages.success'),
    });
    
    // Scroll to form section
    const formSection = document.getElementById('main-title');
    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };
  
  const handleFormLoadingChange = (isLoading: boolean, message?: string) => {
    setLoading({ isLoading, message });
  };
  
  const resetForm = () => {
    setFormSubmitted(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  if (isPageLoading) {
    return <LoadingScreen message={i18n.t('loading.title')} />;
  }
  
  return (
    <div className="min-h-screen flex flex-col bg-[#f9fafb]">
      <Header 
        currentLanguage={currentLanguage}
      />
      
      {alert.visible && (
        <div className="fixed top-5 right-5 z-50 w-80">
          <Alert
            type={alert.type}
            message={alert.message}
            visible={alert.visible}
            onClose={() => setAlert({ ...alert, visible: false })}
            autoClose={true}
            autoCloseTime={5000}
          />
        </div>
      )}
      
      {loading.isLoading && (
        <LoadingScreen message={loading.message} />
      )}
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-[#2c3e50] to-[#3498db] text-white min-h-[1200px] sm:min-h-[1000px] lg:h-[1000px] overflow-hidden">
          <div className="absolute inset-0 bg-[url('/pattern.png')] opacity-20"></div>
          
          {/* Hero Image - Mobile version */}
          <img 
            src="/images/3_no_bg_2.webp" 
            alt="Happy person after receiving finance confirmation" 
            className="absolute top-[15%] left-1/2 -translate-x-1/2 h-[250px] w-auto object-contain lg:hidden z-0"
          />
          
          {/* Hero Image - Desktop version */}
          <img 
            src="/images/3_no_bg_2.webp" 
            alt="Happy person after receiving finance confirmation" 
            className="absolute bottom-0 left-[5%] h-[80%] w-auto object-contain hidden lg:block z-0"
          />

          <div className="container mx-auto px-4 relative z-10 h-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 h-full">
              <div className="space-y-8 pt-8 lg:pt-16">
                <AnimatedUseCases />
                <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight" id="main-title">
                  {i18n.t('hero.title')}
                </h1>
                <p className="text-xl text-white/90 leading-relaxed">
                  {i18n.t('hero.description')}
                </p>
              </div>

              <div className="flex items-center h-full py-4 lg:py-0">
                <div id="loan-form-section" className="relative w-full bg-white/10 backdrop-blur-sm md:p-8 p-4 border border-[#1abc9c]/40 shadow-xl rounded-lg h-auto">
                  {formSubmitted ? (
                    <FormSuccess onClose={resetForm} />
                  ) : (
                    <LoanForm 
                      onSubmit={handleFormSubmit}
                      onLoading={handleFormLoadingChange}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        <Features />
        <LoanTypes />

        {/* About Us Section */}
        <section id={i18n.t('anchor_links.about_us')} className="py-8 bg-white">
          <div className="container mx-auto px-4">
            <AboutUs />
          </div>
        </section>

        {/* FAQ Section */}
        <section id={i18n.t('anchor_links.faq')} className="py-8 bg-gradient-to-br from-[#f9fafb] to-white">
          <div className="container mx-auto px-4">
            <Faq />
          </div>
        </section>

        {/* Contact Section */}
        <section id={i18n.t('anchor_links.contact')} className="py-8 bg-white">
          <div className="container mx-auto px-4">
            <Contact />
          </div>
        </section>
      </main>
      
      <FloatingActionButton />
      <Footer />
    </div>
  );
};

export default LandingPage;